/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useCallback} from 'react';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import Switch, {SwitchProps} from '@material-ui/core/Switch';
import classNames from 'classnames';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

export enum LabelPlacement {
  END = 'end',
  START = 'start'
}

interface StyleProps {
  marginRight?: string;
  marginTop?: string;
  marginLeft?: string;
};

export interface SwitchTypes extends SwitchProps, StyleProps {
  label?: string;
  checked?: boolean;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  labelPlacement?: LabelPlacement;
  isDarkLabel?: boolean;
}

const useStyles = makeStyles(theme => ({
  formControlRoot : {
    justifyContent : 'space-between',
    width          : '100%'
  },
  root : {
    overflow : 'visible',
    '&.*'    : {
      boxSizing : 'border-box'
    }
  },
  labelAlignment : {
    marginRight : ({marginRight}: StyleProps) => marginRight || '-1px',
    marginLeft  : ({marginLeft}: StyleProps) => marginLeft || '1px',
    marginTop   : ({marginTop}: StyleProps) => marginTop || null
  },
  defaultLabel : {
    ...dlsAlt('maps', 'layer', 'listHeader')
  },
  darkLabel : {
    color : `${dlsColor('black')} !important`
  }
}));

const CustomSwitch = withStyles(theme => ({
  switchBase : {
    // [DLS TODO]: Please make sure while the focus component position is not changing.
    // Look like a new 2px border will create another wrapper around the component.
    '&.Mui-focusVisible' : {
      outline : `2px solid ${dlsColor('caBlue')}`
    },
    color     : `${dlsColor('grey3')}`,
    '&:hover' : {
      backgroundColor : 'rgba(130,130,130,0.08)'
    },
    '&$checked' : {
      color     : `${dlsColor('caBlue')}`,
      '&:hover' : {
        backgroundColor : 'rgba(2,171,202,0.08)'
      }
    },
    '&$checked + $track' : {
      backgroundColor : `${dlsColor('caBlue')}`
    },
    '&$disabled' : {
      color : `${dlsColor('grey3')}`
    },
    '&$disabled + $track' : {
      backgroundColor : `${dlsColor('grey3')}`
    }
  },
  input : {
    '&&' : {
      left  : 0,
      width : 'auto'
    }
  },
  checked  : {},
  track    : {},
  disabled : {}
}))(Switch);

/**
 * @Switch
 *
 * Defines the Switch component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 * <Switch onChange={function()} />
 *
 * ```
 */
export const SwitchWrapper = ({
  labelPlacement = LabelPlacement.END,
  checked = false,
  label = '',
  isDarkLabel = true,
  ...props
}: SwitchTypes) => {
  const classes = useStyles(props);
  const {
    marginRight,
    marginLeft,
    marginTop,
    ...rest
  } = props;

  const noop = useCallback(function(event) {
    event.stopPropagation();
  }, []);

  return (
    <FormControlLabel label={label}
      className={classNames(classes.labelAlignment, classes.defaultLabel)}
      classes={(labelPlacement === LabelPlacement.START) ? {
        root  : classes.formControlRoot,
        label : isDarkLabel ? classes.darkLabel : null
      } : {}}
      onClick={noop}
      onFocus={noop}
      value={label}
      control={<CustomSwitch className={classes.root}
        disableFocusRipple
        checked={checked}
        {...rest} />}
      labelPlacement={labelPlacement} />
  );
};

export default SwitchWrapper;
